export type InventoryType =
  | 'WIP'
  | 'DMP'
  | 'PIP'
  | 'DeliveryUnavailable'
  | 'OutOfMarket';

export function isZipCodeInventoryType(inventoryType: InventoryType): boolean {
  return (
    inventoryType === 'DeliveryUnavailable' || inventoryType === 'OutOfMarket'
  );
}

export type InventorySearchOptions = {
  inventoryType: InventoryType;
};

export type InventorySearchResult = {
  inventoryType: InventoryType;
  searchedAt: string;
  totalResults: number;
  vehicles: Array<{
    vehicleId: number;
    lockType: string;
  }>;
  zipCodes?: Array<{
    zipCode: string;
  }>;
};

const INVENTORY_SEARCH_STORAGE_KEY = 'merch-ui-inventory-searches';

function getInventorySearchesFromLocalStorage(): Record<
  string,
  InventorySearchResult
> {
  const stored = localStorage.getItem(INVENTORY_SEARCH_STORAGE_KEY);

  return stored ? JSON.parse(stored) : {};
}

function storeInventorySearchInLocalStorage(
  result: InventorySearchResult
): void {
  const existingSearches = getInventorySearchesFromLocalStorage();
  const key = `${result.inventoryType}_${result.searchedAt}`;
  existingSearches[key] = result;

  localStorage.setItem(
    INVENTORY_SEARCH_STORAGE_KEY,
    JSON.stringify(existingSearches)
  );
}

const temporaryOutOfMarketZips = [
  89421, 97010, 97134, 97143, 97406, 97407, 97411, 97414, 97415, 97420, 97423,
  97427, 97444, 97450, 97458, 97459, 97464, 97465, 97466, 97476, 97484, 97491,
  97497, 97501, 97502, 97503, 97504, 97520, 97522, 97523, 97524, 97525, 97526,
  97527, 97528, 97530, 97531, 97532, 97533, 97534, 97535, 97536, 97537, 97538,
  97539, 97540, 97541, 97543, 97544, 97601, 97602, 97603, 97604, 97620, 97621,
  97622, 97623, 97624, 97625, 97626, 97627, 97630, 97632, 97633, 97634, 97635,
  97636, 97637, 97638, 97639, 97640, 97641, 97709, 97710, 97720, 97721, 97722,
  97731, 97732, 97735, 97736, 97738, 97751, 97752, 97758, 97814, 97817, 97819,
  97820, 97824, 97825, 97827, 97828, 97833, 97834, 97837, 97840, 97841, 97842,
  97845, 97846, 97850, 97856, 97857, 97865, 97867, 97869, 97870, 97873, 97876,
  97877, 97883, 97884, 97885, 97901, 97902, 97903, 97904, 97905, 97906, 97907,
  97908, 97909, 97910, 97911, 97913, 97914, 97917, 97918, 97920
];

const outOfMarketZips = [
  99501, 99502, 99503, 99504, 99505, 99506, 99507, 99508, 99515, 99516, 96701,
  96706, 96707, 96708, 96712, 96713, 96714, 96715, 96716, 96717
];

export async function findInventoryVehicles(
  options: InventorySearchOptions
): Promise<InventorySearchResult> {
  const { inventoryType } = options;

  // Handle DeliveryUnavailable case - return zip codes instead of making API call
  if (inventoryType === 'DeliveryUnavailable') {
    const zipCodes = temporaryOutOfMarketZips.map(zip => ({
      zipCode: zip.toString()
    }));

    const searchResult: InventorySearchResult = {
      inventoryType,
      searchedAt: new Date().toISOString(),
      totalResults: zipCodes.length,
      vehicles: [],
      zipCodes
    };

    storeInventorySearchInLocalStorage(searchResult);

    return searchResult;
  }

  if (inventoryType === 'OutOfMarket') {
    const zipCodes = outOfMarketZips.map(zip => ({
      zipCode: zip.toString()
    }));

    const searchResult: InventorySearchResult = {
      inventoryType,
      searchedAt: new Date().toISOString(),
      totalResults: zipCodes.length,
      vehicles: [],
      zipCodes
    };

    storeInventorySearchInLocalStorage(searchResult);

    return searchResult;
  }

  const response = await fetch(
    'https://apik.carvanatech.com/merch/search/api/v2/search',
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        pagination: {
          page: 1,
          pageSize: 250
        },
        filters:
          inventoryType === 'PIP'
            ? {
                lockTypes: ['Purchase']
              }
            : inventoryType === 'DMP'
            ? {
                inventoryTypes: ['DealerMarketplace']
              }
            : inventoryType === 'WIP'
            ? {
                inventoryTypes: ['WIP']
              }
            : {}
      })
    }
  );

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(
        `API endpoint returned HTML instead of JSON. This might be a CORS or authentication issue.`
      );
    }

    const errorData = await response.json();
    throw new Error(
      `Failed to search inventory: ${response.status} ${
        response.statusText
      } - ${errorData.error || 'Unknown error'}`
    );
  }

  const result = await response.json();
  const vehicles = result.inventory?.vehicles || [];

  const filteredVehicles = vehicles;

  const searchResult: InventorySearchResult = {
    inventoryType,
    searchedAt: new Date().toISOString(),
    totalResults: filteredVehicles.length,
    vehicles: filteredVehicles.map((vehicle: any) => ({
      vehicleId: vehicle.vehicleId,
      lockType:
        vehicle.vehicleLockType === 0
          ? 'None'
          : vehicle.vehicleLockType === 1
          ? 'Soft'
          : vehicle.vehicleLockType === 2
          ? 'Hard'
          : 'Unknown'
    }))
  };

  storeInventorySearchInLocalStorage(searchResult);

  return searchResult;
}
